import dayjs from 'dayjs';
import type { ExtendedInitiative } from '../../../../api/initiative-client/initiative-client.type';
import type { MinifiedTask } from '../../../../api/tasks-client/task-client.type';
import { TaskStatus } from '../../../../api/tasks-client/task-client.type';
import {
  graphAmethyst,
  graphCambridgeBlue,
  graphLightRed,
  graphPictonBlue,
  graphSandyBrown,
  graphTropicalIndigo,
  skyDark,
  skyLight,
} from '../../../../styles/design-tokens';

/**
 * Gets the latest date from initiative performance data
 * If the latest date is in the future, returns the current date instead
 *
 * @param {ExtendedInitiative | null} initiativePerformance - The initiative performance data
 * @returns {Date | null} The latest date or current date if latest is in the future, null if no data
 */
const getLatestDate = (initiativePerformance: ExtendedInitiative | null): Date | null => {
  const dataset = initiativePerformance?.total_subtasks_over_time?.tasks;

  if (!dataset) {
    return null;
  }

  const dates = Object.keys(dataset);

  if (dates.length === 0) {
    return null;
  }

  const latestDateFromData = dayjs(dates[dates.length - 1]);
  const today = dayjs();

  // Check if the latest date is in the future
  if (latestDateFromData.isAfter(today)) {
    // Return the start of the current month to avoid timezone issues
    return today.startOf('month').toDate();
  }

  // Return the start of the month for the latest date to avoid timezone issues
  return latestDateFromData.startOf('month').toDate();
};

/**
 * Gets the latest status from a task's transitions
 *
 * @param {MinifiedTask} task - The task to get the latest status from
 * @returns {TaskStatus | null} The latest status or null if no transitions exist
 */
const getLatestStatus = (task: MinifiedTask): TaskStatus | null => {
  const { transitions = [] } = task;

  if (transitions.length === 0) {
    return null;
  }

  const latestTransition = transitions[transitions.length - 1];

  return latestTransition.status as TaskStatus;
};

/**
 * Filters tasks by a search term, matching against title or name
 *
 * @param {MinifiedTask[]} tasks - The array of tasks to filter
 * @param {string} searchTerm - The search term to match against
 * @returns {MinifiedTask[]} Filtered array of tasks matching the search term
 */
const filterTasksBySearchTerm = (tasks: MinifiedTask[], searchTerm: string): MinifiedTask[] => {
  if (!searchTerm || searchTerm.trim() === '') {
    return tasks;
  }

  const normalizedSearchTerm = searchTerm.toLowerCase().trim();

  return tasks.filter((task) => {
    const titleMatch = task.title && task.title.toLowerCase().includes(normalizedSearchTerm);
    const nameMatch = task.name && task.name.toLowerCase().includes(normalizedSearchTerm);

    return titleMatch || nameMatch;
  });
};

/**
 * Gets the color for a given task status
 *
 * @param {TaskStatus | null} status - The task status to get the color for
 * @returns {string} The color for the given status
 */
const getTaskStatusColor = (status: TaskStatus | null): string => {
  if (!status) {
    return skyDark;
  }

  const statusColors = {
    [TaskStatus.Blocked]: skyDark,
    [TaskStatus.Backlog]: graphTropicalIndigo,
    [TaskStatus.WIP]: graphLightRed,
    [TaskStatus.Review]: graphPictonBlue,
    [TaskStatus.Test]: graphSandyBrown,
    [TaskStatus.Deployable]: graphAmethyst,
    [TaskStatus.Done]: graphCambridgeBlue,
  };

  return statusColors[status] || skyLight;
};

/**
 * Collects unique task types from an array of tasks
 *
 * @param {MinifiedTask[] | undefined} tasks - The array of tasks to collect types from
 * @returns {string[]} Array of unique task types
 */
const collectTaskTypes = (tasks: MinifiedTask[] | undefined): string[] => {
  if (!tasks) {
    return [];
  }

  return [...new Set(tasks.map((task) => task.type))].sort();
};

export { collectTaskTypes, filterTasksBySearchTerm, getLatestDate, getLatestStatus, getTaskStatusColor };
